import React from 'react';
import Html from 'components/html';
import { sendTracking } from 'services/google-analytics';

import tracking from '../start-earning.tracking';

import { testId, sizes, minSwipeDistance } from './mobile-carousel.settings';
import styles from './mobile-carousel.module.scss';

export interface MobileCarouselProps {
  slides: Array<string>;
  onSlideChange: (index: number) => void;
}

const MobileCarousel: React.FunctionComponent<MobileCarouselProps> = (props) => {
  const { slides, onSlideChange } = props;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const touchStartXRef = React.useRef<number | null>(null);
  const touchEndXRef = React.useRef<number | null>(null);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [containerWidth, setContainerWidth] = React.useState(0);

  React.useEffect(() => {
    const handleResize = (): void => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getNormalizedIndex = React.useCallback(
    (index: number) => {
      const totalSlides = slides.length;

      return ((index % totalSlides) + totalSlides) % totalSlides;
    },
    [slides.length]
  );

  const visibleSlides = React.useMemo(() => {
    onSlideChange(getNormalizedIndex(activeIndex));

    const visibleIndexes = [-2, -1, 0, 1, 2].map((offset) => {
      const actualIndex = getNormalizedIndex(activeIndex + offset);

      return {
        slide: slides[actualIndex],
        virtualIndex: activeIndex + offset,
        actualIndex,
      };
    });

    return visibleIndexes;
  }, [activeIndex, getNormalizedIndex, slides, onSlideChange]);

  const handleTouchStart = React.useCallback((e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchEndXRef.current = null;
  }, []);

  const handleTouchMove = React.useCallback((e: React.TouchEvent) => {
    touchEndXRef.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = React.useCallback(() => {
    if (!touchStartXRef.current || !touchEndXRef.current) return;

    const swipeDistance = touchEndXRef.current - touchStartXRef.current;

    if (Math.abs(swipeDistance) > minSwipeDistance) {
      if (swipeDistance > 0) {
        sendTracking(tracking.navSwipePrevious);
        setActiveIndex((prevIndex) => prevIndex - 1);
      } else {
        sendTracking(tracking.navSwipeNext);
        setActiveIndex((prevIndex) => prevIndex + 1);
      }
    }

    touchStartXRef.current = null;
    touchEndXRef.current = null;
  }, []);

  const getSlideStyle = React.useCallback(
    (virtualIndex: number): React.CSSProperties => {
      const position = virtualIndex - activeIndex;
      const centerOffset = containerWidth / 2 - sizes.active.width / 2;
      const positiveOffset = containerWidth / 2 + sizes.active.width / 2 + sizes.spacing;
      const negativeOffset = centerOffset - sizes.spacing - sizes.inactive.width;

      let translateX = centerOffset;

      if (position > 0) {
        const slideOffset = (position - 1) * (sizes.inactive.width + sizes.spacing);

        translateX = positiveOffset + slideOffset;
      }

      if (position < 0) {
        const slideOffset = (position + 1) * (sizes.inactive.width + sizes.spacing);

        translateX = negativeOffset + slideOffset;
      }

      const translateY = position === 0 ? 0 : sizes.active.height - sizes.inactive.height;

      return { transform: `translate(${translateX}px, ${translateY}px)` } satisfies React.CSSProperties;
    },
    [activeIndex, containerWidth]
  );

  return (
    <Html.div
      className={styles.container}
      testId={testId.component}
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {visibleSlides.map(({ slide, virtualIndex, actualIndex }) => {
        return (
          <Html.div
            key={`slide-${actualIndex}-${virtualIndex}`}
            className={[styles.slide, activeIndex === virtualIndex && styles.active]}
            style={getSlideStyle(virtualIndex)}
            role="img"
            aria-label={`Slide ${actualIndex + 1}`}
            testId={testId.slide}
          >
            <img src={slide} alt={`${actualIndex + 1}`} />
          </Html.div>
        );
      })}
    </Html.div>
  );
};

export default MobileCarousel;
